import api from './api';
import { NewsArticle } from './newsService';

// WICHTIG: ALLES vom Backend muss nullable geparst werden (Backwards Compatibility)!

export type AutomationSourceType = 'DARTCONNECT_TOURNAMENT' | 'MSS_SERIES' | 'WEBSITE';

export interface AutomationSourceConfig {
  tournamentId?: string | null;
  seriesId?: number | null;
  country?: string | null;
  url?: string | null;
  maxItems?: number | null;
}

export interface AutomationSource {
  id?: string | null;
  _id?: string | null;
  name?: string | null;
  type?: AutomationSourceType | string | null;
  config?: AutomationSourceConfig | null;
  enabled?: boolean | null;
  lastCheckedAt?: number | null;
  lastError?: string | null;
  createdAt?: number | null;
}

export interface AutomationSettings {
  enabled?: boolean | null;
  intervalMinutes?: number | null;
  autoPublish?: boolean | null;
  maxArticlesPerRun?: number | null;
  claudeModel?: string | null;
  /** Eingebauter PDC-Scan über die DartConnect-API (ohne konfigurierte Quelle) */
  pdcAutoDiscovery?: boolean | null;
  lastRunAt?: number | null;
}

export type AutomationRunStatus = 'running' | 'success' | 'error';

export interface AutomationRun {
  id?: string | null;
  _id?: string | null;
  startedAt?: number | null;
  finishedAt?: number | null;
  status?: AutomationRunStatus | string | null;
  triggeredBy?: string | null;
  articlesCreated?: number | null;
  topics?: string[] | null;
  errors?: string[] | null;
}

/** KI-generierter News-Artikel im Review-Status. */
export interface PendingArticle extends NewsArticle {
  aiGenerated?: boolean | null;
  reviewStatus?: string | null;
}

export interface RunsResponse {
  runs: AutomationRun[];
  total: number;
}

export interface PendingResponse {
  articles: PendingArticle[];
  total: number;
}

function safeArray<T>(value: unknown): T[] {
  if (!value || !Array.isArray(value)) return [];
  return value.filter((item) => item && typeof item === 'object') as T[];
}

function safeStringArray(value: unknown): string[] {
  if (!value || !Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string');
}

class AdminNewsAutomationService {
  // ---------- Quellen ----------

  async getSources(): Promise<AutomationSource[]> {
    const response = await api.get('/api/admin/news-automation/sources');
    return safeArray<AutomationSource>(response.data?.sources);
  }

  async createSource(payload: {
    name: string;
    type: AutomationSourceType;
    config?: AutomationSourceConfig;
    enabled?: boolean;
  }): Promise<AutomationSource | null> {
    const response = await api.post('/api/admin/news-automation/sources', payload);
    return (response.data?.source as AutomationSource) ?? null;
  }

  async updateSource(
    id: string,
    payload: Partial<{
      name: string;
      type: AutomationSourceType;
      config: AutomationSourceConfig;
      enabled: boolean;
    }>
  ): Promise<AutomationSource | null> {
    const response = await api.put(`/api/admin/news-automation/source/${id}`, payload);
    return (response.data?.source as AutomationSource) ?? null;
  }

  async deleteSource(id: string): Promise<boolean> {
    const response = await api.delete(`/api/admin/news-automation/source/${id}`);
    return response.data?.success === true;
  }

  // ---------- Einstellungen ----------

  async getSettings(): Promise<AutomationSettings> {
    const response = await api.get('/api/admin/news-automation/settings');
    return (response.data?.settings as AutomationSettings) ?? {};
  }

  async updateSettings(payload: Partial<{
    enabled: boolean;
    intervalMinutes: number;
    autoPublish: boolean;
    maxArticlesPerRun: number;
    claudeModel: string;
    pdcAutoDiscovery: boolean;
  }>): Promise<AutomationSettings> {
    const response = await api.put('/api/admin/news-automation/settings', payload);
    return (response.data?.settings as AutomationSettings) ?? {};
  }

  // ---------- Läufe ----------

  async triggerRun(): Promise<AutomationRun | null> {
    const response = await api.post('/api/admin/news-automation/run');
    return (response.data?.run as AutomationRun) ?? null;
  }

  async getRuns(params?: { limit?: number; offset?: number }): Promise<RunsResponse> {
    const response = await api.get('/api/admin/news-automation/runs', {
      params: {
        limit: params?.limit ?? 10,
        offset: params?.offset ?? 0,
      },
    });
    const runs = safeArray<AutomationRun>(response.data?.runs).map((run) => ({
      ...run,
      topics: safeStringArray(run.topics),
      errors: safeStringArray(run.errors),
    }));
    const total =
      typeof response.data?.total === 'number' ? response.data.total : runs.length;
    return { runs, total };
  }

  // ---------- Review ----------

  async getPendingArticles(params?: {
    limit?: number;
    offset?: number;
  }): Promise<PendingResponse> {
    const response = await api.get('/api/admin/news-automation/pending', {
      params: {
        limit: params?.limit ?? 12,
        offset: params?.offset ?? 0,
      },
    });
    const articles = safeArray<PendingArticle>(response.data?.articles);
    const total =
      typeof response.data?.total === 'number' ? response.data.total : articles.length;
    return { articles, total };
  }

  async approveArticle(id: string): Promise<PendingArticle | null> {
    const response = await api.post(`/api/admin/news-automation/article/${id}/approve`);
    return (response.data?.article as PendingArticle) ?? null;
  }

  async rejectArticle(id: string, reason?: string): Promise<PendingArticle | null> {
    const response = await api.post(
      `/api/admin/news-automation/article/${id}/reject`,
      reason && reason.trim() ? { reason: reason.trim() } : {}
    );
    return (response.data?.article as PendingArticle) ?? null;
  }

  // ---------- Helpers ----------

  /** ID einer Quelle / eines Runs / Artikels robust ermitteln. */
  getId(entity?: { _id?: string | null; id?: string | null } | null): string | null {
    if (!entity) return null;
    return entity._id || entity.id || null;
  }

  /**
   * Fehlermeldung aus einem Axios-Fehler extrahieren.
   * 4xx-Fehler kommen als PLAINTEXT-Body vom Backend.
   */
  getErrorMessage(error: unknown, fallback: string): string {
    const anyError = error as any;
    const data = anyError?.response?.data;
    if (typeof data === 'string' && data.trim()) return data.trim();
    if (data && typeof data === 'object') {
      if (typeof data.message === 'string' && data.message.trim()) {
        return data.message.trim();
      }
      if (typeof data.error === 'string' && data.error.trim()) {
        return data.error.trim();
      }
    }
    return fallback;
  }
}

export default new AdminNewsAutomationService();
